'use client';

import { HTMLProps } from '@/lib/util/types';
import clsx from 'clsx';
import { DetailedHTMLProps, InputHTMLAttributes, ReactElement, useId, useState } from 'react';
import styles from './CheckBox.module.scss';

export type CheckBoxProps = {
  label?: ReactElement | string;
  uncheckedIcon?: ReactElement | string;
  checkedIcon?: ReactElement | string;
  checked?: boolean;
  slotProps?: {
    input?: HTMLProps<HTMLInputElement>;
    label?: HTMLProps<HTMLSpanElement>;
    checkBoxBase: HTMLProps<HTMLDivElement>;
    checkBoxSlider: HTMLProps<HTMLDivElement>;
  };
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'checked' | 'onChange'
>;

export default function CheckBox({
  label,
  uncheckedIcon,
  checkedIcon,
  checked,
  defaultChecked,
  onChange,
  style,
  slotProps,
  id,
  ...props
}: CheckBoxProps) {
  const {
    input: inputProps,
    label: labelProps,
    checkBoxBase: checkBoxBaseProps,
    checkBoxSlider: checkBoxSliderProps,
  } = slotProps || {};

  const internalId = useId();
  const trueId = id || internalId;
  const [uncontrolledChecked, setUncontrolledChecked] = useState<boolean>(Boolean(defaultChecked));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    checked !== undefined
      ? onChange?.(e.target.checked, e)
      : setUncontrolledChecked(e.target.checked);

  const trueChecked = checked !== undefined ? checked : uncontrolledChecked;

  return (
    <label className={styles.field} htmlFor={trueId} style={style}>
      <input
        id={trueId}
        type="checkbox"
        checked={trueChecked}
        onChange={handleChange}
        style={{ visibility: 'hidden', width: 0, height: 0 }}
        {...inputProps}
        {...props}
      />
      <div className={clsx(styles.checkBoxBase, { checked: trueChecked })} {...checkBoxBaseProps}>
        <div className={styles.checkBoxSlider} {...checkBoxSliderProps}>
          {trueChecked ? checkedIcon : uncheckedIcon}
        </div>
      </div>
      {label && (
        <span className={clsx(styles.label, { checked: trueChecked })} {...labelProps}>
          {label}
        </span>
      )}
    </label>
  );
}
